import { Flame } from "lucide-react";
import Mascot from "@/components/Mascot";
import BadgeImage, { BADGE_IMG } from "@/components/BadgeImage";

export default function Progress({ state }) {
  const earned = new Set(state.achievements.earned.map((a) => a.code));
  const level = state.level;
  const pct = Math.min(100, Math.round(((level.current_xp - level.level_min_xp) / Math.max(1, level.level_max_xp - level.level_min_xp)) * 100));
  const activity = state.activity;

  return (
    <div className="pb-28 pt-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="micro-label">Your journey</p>
          <h1 className="text-[32px] lg:text-[44px] text-[hsl(var(--ink))] leading-tight">Progress</h1>
        </div>
        <Mascot state={state.achievements.earned.length > 0 ? "achievement" : "happy"} size={120} testId="progress-mascot" className="-mb-2"/>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] lg:gap-8 items-start">
        {/* LEFT: level + stats + activity */}
        <div className="space-y-4 lg:sticky lg:top-6">
          <section className="surface-violet px-5 py-5">
            <div className="flex items-end justify-between">
              <div>
                <p className="micro-label text-[hsl(var(--violet-deep))]">Level</p>
                <p className="text-5xl font-semibold text-[hsl(var(--ink))] mt-1">{level.level}</p>
              </div>
              <div className="text-right">
                <p className="micro-label">Total XP</p>
                <p className="text-3xl font-semibold text-[hsl(var(--violet-deep))]" data-testid="total-xp">{state.xp}</p>
              </div>
            </div>
            <div className="mt-3 h-2.5 bg-white/70 rounded-full overflow-hidden">
              <div className="h-full bg-[hsl(var(--violet))] rounded-full" style={{ width: `${pct}%` }}/>
            </div>
            <p className="text-xs text-right mt-1 font-semibold text-[hsl(var(--ink-soft))]">{level.current_xp} / {level.level_max_xp} XP</p>
          </section>

          <section className="grid grid-cols-2 gap-3">
            <div className="surface-soft p-4">
              <p className="micro-label">Current streak</p>
              <p className="text-3xl font-semibold text-[hsl(var(--ink))] flex items-center gap-2">
                <Flame className="w-6 h-6 text-[hsl(var(--amber-deep))]" strokeWidth={2.2}/>{state.streak}
              </p>
              <p className="text-xs text-[hsl(var(--ink-soft))]">weeks</p>
            </div>
            <div className="surface-soft p-4">
              <p className="micro-label">Longest</p>
              <p className="text-3xl font-semibold text-[hsl(var(--ink))]">{state.longest_streak}</p>
              <p className="text-xs text-[hsl(var(--ink-soft))]">weeks</p>
            </div>
          </section>

          <section>
            <h2 className="text-[20px] text-[hsl(var(--ink))] mb-3">This month</h2>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Drum practices" value={state.month.drum_sessions}/>
              <Stat label="Drum minutes" value={state.month.drum_minutes}/>
              <Stat label="English quests" value={state.month.english_quests}/>
              <Stat label="Perfect weeks" value={state.month.perfect_weeks}/>
            </div>
          </section>

          {activity.length > 0 && (
            <section className="surface-soft p-4">
              <h2 className="micro-label mb-3">Recent weeks</h2>
              <div className="flex items-end gap-2 h-28" data-testid="activity-chart">
                {activity.map((w) => {
                  const total = w.english_done + w.drum_done;
                  const h = Math.min(100, (total / 5) * 100);
                  return (
                    <div key={w.start_date} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className={`w-full rounded-t-xl ${w.perfect ? "bg-[hsl(var(--violet))]" : total ? "bg-[hsl(var(--amber))]" : "bg-[hsl(var(--surface-2))]"}`}
                        style={{ height: `${h}%` }}
                      />
                      <span className="text-[10px] font-semibold text-[hsl(var(--ink-mute))]">
                        {new Date(w.start_date).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* RIGHT: achievements */}
        <section className="mt-6 lg:mt-0">
          <h2 className="text-[20px] lg:text-[24px] text-[hsl(var(--ink))] mb-3">Achievements</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="achievements-grid">
            {state.achievements.all.map((a) => {
              const have = earned.has(a.code);
              const hasBadge = !!BADGE_IMG[a.code];
              return (
                <div key={a.code} className={`surface-soft p-4 text-center ${have ? "" : "opacity-80"}`} data-testid={`ach-${a.code}`}>
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center
                    ${have
                      ? "bg-gradient-to-b from-[hsl(var(--amber-wash))] to-white shadow-[0_14px_28px_-10px_hsla(36,70%,52%,0.55),inset_0_1px_0_rgba(255,255,255,0.9)]"
                      : "bg-[hsl(var(--surface-2))]"}`}>
                    {hasBadge
                      ? <BadgeImage code={a.code} size={72} earned={have}/>
                      : <span className={`text-xs font-semibold ${have ? "text-[hsl(var(--amber-deep))]" : "text-[hsl(var(--ink-mute))]"}`}>?</span>
                    }
                  </div>
                  <p className="mt-2.5 text-sm font-semibold text-[hsl(var(--ink))]">{a.name}</p>
                  <p className="text-[11px] text-[hsl(var(--ink-soft))] mt-0.5">{a.description}</p>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface-soft p-4">
      <p className="micro-label">{label}</p>
      <p className="text-2xl font-semibold text-[hsl(var(--ink))] mt-1">{value}</p>
    </div>
  );
}
